import { useState } from 'react';
import {
  BookOpenIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

import { semanticColors } from '@/theme/tokens';
import VoteMateLogo from '@/ui-library/VoteMateLogo';

import { getFaviconUrl } from './SourceTypes';
import type { SourceConnectorGlyph, SourceConnectorMark } from './SourceConnectors';

/**
 * The circular mark beside a source connector — the same three-way rule as
 * `ToolCategoryIcon` (VoteMate logo / site favicon / glyph), sized for a list
 * row rather than a research step. Decorative: the row's text names the
 * source, so the mark is hidden from assistive tech.
 */

const GLYPHS: Record<SourceConnectorGlyph, typeof GlobeAltIcon> = {
  book: BookOpenIcon,
  ballot: ClipboardDocumentCheckIcon,
  capitol: BuildingLibraryIcon,
  globe: GlobeAltIcon,
  chart: ChartBarIcon,
};

interface SourceConnectorMarkViewProps {
  mark: SourceConnectorMark;
  size?: number;
}

export default function SourceConnectorMarkView({ mark, size = 36 }: SourceConnectorMarkViewProps) {
  // The favicon service 404s consistently when it fails, so fall back for good.
  const [faviconFailed, setFaviconFailed] = useState(false);
  const faviconUrl =
    mark.kind === 'favicon' && !faviconFailed ? getFaviconUrl(`https://${mark.host}`) : undefined;
  const glyph: SourceConnectorGlyph =
    mark.kind === 'glyph' ? mark.glyph : mark.kind === 'favicon' ? mark.fallbackGlyph : 'globe';
  const Glyph = GLYPHS[glyph];
  const glyphSize = Math.round(size * 0.55);

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        overflow: 'hidden',
        backgroundColor: semanticColors.surface,
        color: semanticColors.textCaption,
        border: `1px solid ${semanticColors.borderLight}`,
      }}
    >
      {mark.kind === 'votemate' ? (
        <VoteMateLogo size={Math.round(size * 0.6)} aria-hidden />
      ) : faviconUrl ? (
        <img
          src={faviconUrl}
          alt=""
          width={Math.round(size * 0.6)}
          height={Math.round(size * 0.6)}
          style={{ display: 'block', objectFit: 'contain' }}
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <Glyph style={{ width: glyphSize, height: glyphSize }} />
      )}
    </span>
  );
}
